import type { Object3D } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { ModelManager } from '../model-manager.class';
import type { ModelLoadOptions } from '../types';
import { applyShadows, applyWireframeToModel, applyEnvironmentMapToModel, adjustSingleMaterial } from '../helpers';

export interface LoadGLTFParams {
	options: ModelLoadOptions;
}

/**
 * Load a GLTF/GLB model
 */
export const loadGLTFCfn = (self: ModelManager, params: LoadGLTFParams): Promise<Object3D> => {
	const { options } = params;
	const { path, position, rotation, scale, metadata, parent, castShadow, receiveShadow } = options;
	const config = self.config;
	const fullPath = `${config.basePath}${path}`;

	return new Promise((resolve, reject) => {
		const loader = new GLTFLoader();

		loader.load(
			fullPath,
			(gltf) => {
				const model = gltf.scene;

				if (position) {
					model.position.set(position.x, position.y, position.z);
				}
				if (rotation) {
					model.rotation.set(rotation.x, rotation.y, rotation.z);
				}
				if (scale) {
					if (typeof scale === 'number') {
						model.scale.setScalar(scale);
					} else {
						model.scale.set(scale.x, scale.y, scale.z);
					}
				}

				if (metadata) {
					model.userData = { ...model.userData, ...metadata };
				}

				applyShadows(model, config.qualitySettings, castShadow, receiveShadow);
				adjustModelMaterials(model, config.materialsMap);

				if (self.wireframeMode) {
					applyWireframeToModel(model, true);
				}

				if (self.environmentMap) {
					applyEnvironmentMapToModel(model, self.environmentMap);
				}

				if (parent) {
					parent.add(model);
				}

				if (metadata?.id) {
					self.loadedModels.set(metadata.id, model);
				}

				self.gltfCache.set(path, model);
				resolve(model);
			},
			undefined,
			(error) => {
				console.error('Error loading GLTF model:', path, error);
				reject(error);
			}
		);
	});
};

const adjustModelMaterials = (model: Object3D, materialsMap?: Map<string, any>): void => {
	if (!materialsMap) return;

	model.traverse((child: any) => {
		if (child.type === 'Mesh' && child.material) {
			if (Array.isArray(child.material)) {
				child.material = child.material.map((m: any) => adjustSingleMaterial(m, materialsMap));
			} else {
				child.material = adjustSingleMaterial(child.material, materialsMap);
			}
		}
	});
};
